import { useEffect, useState } from 'react'
import { deleteView, listViews, saveView, updateView } from '../api'
import { describeFilters } from '../filterSpec'
import { n } from '../format'

const DUP_PHRASE = { duplicates: 'duplicates by', dedupe: 'deduplicated by', unique: 'unique by' }

/** One-line description of what a view will show, mirroring the server's summarize(). */
export function describeView(view) {
  const parts = [describeFilters(view.filters ?? { rules: [] })]
  if (view.dupColumns?.length) {
    parts.push(`${DUP_PHRASE[view.dupMode] ?? DUP_PHRASE.duplicates} ${view.dupColumns.join(' + ')}`)
  }
  if (view.insightColumns?.length) parts.push(`insights by ${view.insightColumns.join(', ')}`)
  if (Number.isFinite(view.rowCount)) parts.push(`${n(view.rowCount)} rows when saved`)
  return parts.join(' · ')
}

/**
 * Saved views for the open file: name the current filters (plus duplicate key and
 * hidden columns) and come back to them later.
 *
 * Two ways to write one: `Save` creates (or overwrites the view of that exact
 * name), and `Update` overwrites one specific view by id — which is what you
 * want after opening a view and tweaking it, and the only path that can rename.
 *
 * @param active the saved view this session came from, `{id, name}` or null
 */
export function SavedViews({ dataset, current, onApply, active, setActive }) {
  const [views, setViews] = useState([])
  const [name, setName] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const refresh = () => {
    listViews(dataset.path)
      .then((r) => setViews(r.views))
      .catch((e) => setError(e.message))
  }

  useEffect(() => {
    setName('')
    setError(null)
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset.path])

  /** @param target when set, overwrite that view by id instead of creating one */
  const write = async (target, newName) => {
    setBusy(true)
    setError(null)
    try {
      const body = { path: dataset.path, ...current() }
      const { view } = target
        ? await updateView(target.id, { ...body, name: newName ?? target.name })
        : await saveView({ ...body, name: newName })
      setActive({ id: view.id, name: view.name })
      setName('')
      refresh()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id) => {
    try {
      await deleteView(id)
      setViews((vs) => vs.filter((v) => v.id !== id))
      if (active?.id === id) setActive(null)
    } catch (err) {
      setError(err.message)
    }
  }

  const trimmed = name.trim()
  const sameNamed = views.find((v) => v.name.toLowerCase() === trimmed.toLowerCase())
  const canUpdate = !!active && views.some((v) => v.id === active.id)
  // Typing a name always writes a new view (or overwrites the one already using
  // that name). Renaming the view you opened is the deliberate, separate button —
  // "save as v2" must never quietly retitle v1.
  const canRename = canUpdate && !!trimmed && !sameNamed

  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Saved views</h2>
        {views.length > 0 && <span className="count">{views.length}</span>}
      </header>

      <form
        className="save-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (trimmed) write(null, trimmed)
          else if (canUpdate) write(active)
        }}
      >
        <input
          className="input"
          placeholder={canUpdate ? `Save a copy of “${active.name}” as…` : 'Name this view…'}
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
        />
        <button className="btn primary" type="submit" disabled={busy || (!trimmed && !canUpdate)}>
          {trimmed ? (sameNamed ? 'Overwrite' : 'Save') : canUpdate ? 'Update' : 'Save'}
        </button>
        {canRename && (
          <button
            className="btn subtle"
            type="button"
            disabled={busy}
            title={`Rename “${active.name}” to “${trimmed}” instead of saving a second view`}
            onClick={() => write(active, trimmed)}
          >
            Rename
          </button>
        )}
      </form>
      {trimmed && sameNamed && <p className="footnote">Overwrites the existing “{sameNamed.name}”.</p>}
      {!trimmed && canUpdate && (
        <p className="footnote">
          Updates “{active.name}” with the filters, duplicate key, insights and visible columns as they
          are now. Name it instead to save a separate view.
        </p>
      )}
      {error && <div className="alert">{error}</div>}

      {views.length === 0 ? (
        <p className="hint">
          Nothing saved for this file yet. Saving keeps the filters, the duplicate key, the insight
          columns and which columns are visible.
        </p>
      ) : (
        <ul className="view-list">
          {views.map((view) => (
            <li key={view.id}>
              <button
                className={`view-item ${active?.id === view.id ? 'on' : ''}`}
                onClick={() => onApply(view)}
                title={`Apply “${view.name}”`}
              >
                <span className="view-name">{view.name}</span>
                <span className="view-meta">{describeView(view)}</span>
              </button>
              <button
                className="icon-btn neutral"
                disabled={busy}
                title={`Update “${view.name}” to the current setup`}
                onClick={() => write(view)}
              >
                ⟳
              </button>
              <button className="icon-btn" title="Delete this view" onClick={() => remove(view.id)}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
